import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Checklist,
  Close,
  CloudUpload,
  CreateNewFolder,
  Delete,
  DeleteSweep,
  Download,
  Folder,
  InsertDriveFile,
  Refresh,
  Search,
  Visibility,
  ViewList,
  ViewModule,
} from '@mui/icons-material';
import { FolderItem, FolderListing, foldersAPI } from '../services/api';

type SortBy = 'name' | 'size' | 'modified' | 'type';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'list' | 'grid';

interface PreviewState {
  item: FolderItem;
  url: string;
  contentType: string;
}

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];

const isImageFile = (item: FolderItem): boolean =>
  item.type === 'file' &&
  (item.mime_type?.startsWith('image/') || IMAGE_EXTENSIONS.includes(item.extension?.toLowerCase() || ''));

const ImageThumbnail: React.FC<{ item: FolderItem }> = ({ item }) => {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isImageFile(item)) return undefined;

    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [item]);

  useEffect(() => {
    let active = true;
    let objectUrl = '';

    if (!visible || !isImageFile(item)) return undefined;

    setFailed(false);
    foldersAPI.thumbnail(item.path, 320)
      .then((response) => {
        if (!active) return;

        const responseContentType = response.headers['content-type'];
        const contentType = typeof responseContentType === 'string' ? responseContentType : 'image/*';
        objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [visible, item]);

  if (!isImageFile(item) || failed) {
    return (
      <Box ref={containerRef} display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
        <InsertDriveFile sx={{ fontSize: 56 }} color="disabled" />
      </Box>
    );
  }

  if (!url) {
    return (
      <Box ref={containerRef} display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
        {visible ? <CircularProgress size={28} /> : <InsertDriveFile sx={{ fontSize: 56 }} color="disabled" />}
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt={item.name}
      loading="lazy"
      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

const FoldersPage: React.FC = () => {
  const [listing, setListing] = useState<FolderListing | null>(null);
  const [currentPath, setCurrentPath] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [showHidden, setShowHidden] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [openingPath, setOpeningPath] = useState('');
  const [previewingPath, setPreviewingPath] = useState('');
  const [downloadingPath, setDownloadingPath] = useState('');
  const [deletingPath, setDeletingPath] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const breadcrumbs = useMemo(() => {
    if (!currentPath) return [{ label: 'Root', path: '' }];

    const parts = currentPath.split('/').filter(Boolean);
    return [
      { label: 'Root', path: '' },
      ...parts.map((part, index) => ({
        label: part,
        path: parts.slice(0, index + 1).join('/'),
      })),
    ];
  }, [currentPath]);

  const loadFiles = async (path = currentPath, actionPath = '') => {
    try {
      setLoading(true);
      if (actionPath) setOpeningPath(actionPath);
      setError('');
      const response = await foldersAPI.list({
        path,
        search,
        sort_by: sortBy,
        sort_order: sortOrder,
        show_hidden: showHidden,
      });
      setListing(response.data.data);
      setCurrentPath(response.data.data.path);
      setSelectedPaths(new Set());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load folder');
    } finally {
      setLoading(false);
      if (actionPath) setOpeningPath('');
    }
  };

  useEffect(() => {
    loadFiles(currentPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortOrder, showHidden]);

  useEffect(() => {
    return () => {
      if (preview?.url) {
        window.URL.revokeObjectURL(preview.url);
      }
    };
  }, [preview]);

  const openFolder = (item: FolderItem) => {
    if (item.type === 'directory') {
      loadFiles(item.path, item.path);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      setCreatingFolder(true);
      setError('');
      await foldersAPI.createDirectory(currentPath, newFolderName.trim());
      setSuccess('Folder created');
      setNewFolderName('');
      setCreateDialogOpen(false);
      loadFiles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    try {
      setUploading(true);
      setError('');
      await foldersAPI.upload(currentPath, files);
      setSuccess(`Uploaded ${files.length} file(s)`);
      loadFiles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload file(s)');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownload = async (item: FolderItem) => {
    try {
      setDownloadingPath(item.path);
      setError('');
      const response = await foldersAPI.download(item.path);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download file');
    } finally {
      setDownloadingPath('');
    }
  };

  const handlePreview = async (item: FolderItem) => {
    try {
      setPreviewingPath(item.path);
      setError('');
      const response = await foldersAPI.preview(item.path);
      const responseContentType = response.headers['content-type'];
      const contentType =
        typeof responseContentType === 'string'
          ? responseContentType
          : item.mime_type || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      setPreview((current) => {
        if (current?.url) window.URL.revokeObjectURL(current.url);
        return { item, url, contentType };
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to preview file');
    } finally {
      setPreviewingPath('');
    }
  };

  const closePreview = () => {
    if (preview?.url) {
      window.URL.revokeObjectURL(preview.url);
    }
    setPreview(null);
  };

  const handleDelete = async (item: FolderItem) => {
    const target = item.type === 'directory' ? 'folder and all its contents' : 'file';
    if (!window.confirm(`Delete this ${target}: ${item.name}?`)) return;

    try {
      setDeletingPath(item.path);
      setError('');
      await foldersAPI.delete(item.path);
      setSuccess('Deleted successfully');
      loadFiles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete item');
    } finally {
      setDeletingPath('');
    }
  };

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) setSelectedPaths(new Set());
      return !prev;
    });
  };

  const toggleSelected = (path: string) => {
    setSelectedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const items = listing?.items ?? [];
  const allSelected = items.length > 0 && items.every((item) => selectedPaths.has(item.path));
  const someSelected = items.some((item) => selectedPaths.has(item.path));

  const toggleSelectAll = () => {
    setSelectedPaths((prev) => {
      if (items.length > 0 && items.every((item) => prev.has(item.path))) {
        return new Set();
      }
      return new Set(items.map((item) => item.path));
    });
  };

  const handleBulkDelete = async () => {
    const paths = Array.from(selectedPaths);
    if (paths.length === 0) return;
    if (
      !window.confirm(
        `Delete ${paths.length} selected item(s)? Folders will be deleted with all their contents.`
      )
    ) {
      return;
    }

    setBulkDeleting(true);
    setError('');
    let failures = 0;
    for (const path of paths) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await foldersAPI.delete(path);
      } catch {
        failures += 1;
      }
    }
    setBulkDeleting(false);
    setSelectionMode(false);

    if (failures) {
      setError(`Deleted ${paths.length - failures} item(s); ${failures} failed`);
    } else {
      setSuccess(`Deleted ${paths.length} item(s)`);
    }
    loadFiles();
  };

  const storage = listing?.storage;

  const renderPreview = () => {
    if (!preview) return null;

    const { item, url, contentType } = preview;
    const extension = item.extension?.toLowerCase();
    const isImage = contentType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension || '');
    const isPdf = contentType === 'application/pdf' || extension === 'pdf';
    const isVideo = contentType.startsWith('video/');
    const isAudio = contentType.startsWith('audio/');
    const isText = contentType.startsWith('text/') || ['txt', 'csv', 'json', 'xml', 'log', 'md', 'yml', 'yaml'].includes(extension || '');

    if (isImage) {
      return (
        <Box
          component="img"
          src={url}
          alt={item.name}
          sx={{ maxWidth: '100%', maxHeight: '75vh', display: 'block', mx: 'auto' }}
        />
      );
    }

    if (isPdf || isText) {
      return <Box component="iframe" src={url} title={item.name} sx={{ width: '100%', height: '75vh', border: 0 }} />;
    }

    if (isVideo) {
      return <Box component="video" src={url} controls sx={{ width: '100%', maxHeight: '75vh' }} />;
    }

    if (isAudio) {
      return <Box component="audio" src={url} controls sx={{ width: '100%' }} />;
    }

    return (
      <Box textAlign="center" py={6}>
        <InsertDriveFile sx={{ fontSize: 64, mb: 2 }} color="disabled" />
        <Typography variant="h6" gutterBottom>
          Preview is not available for this file type
        </Typography>
        <Typography color="textSecondary" mb={2}>
          {contentType}
        </Typography>
        <Button variant="contained" startIcon={<Download />} onClick={() => handleDownload(item)}>
          Download File
        </Button>
      </Box>
    );
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Folders
          </Typography>
          <Typography color="textSecondary">
            Manage files directly from the configured Ubuntu folder.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <input ref={fileInputRef} type="file" multiple hidden onChange={handleUpload} />
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress color="inherit" size={18} /> : <CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Upload
          </Button>
          <Button variant="outlined" startIcon={<CreateNewFolder />} onClick={() => setCreateDialogOpen(true)}>
            New Folder
          </Button>
          <Button
            variant={selectionMode ? 'contained' : 'outlined'}
            color={selectionMode ? 'secondary' : 'primary'}
            startIcon={selectionMode ? <Close /> : <Checklist />}
            onClick={toggleSelectionMode}
          >
            {selectionMode ? 'Cancel' : 'Select'}
          </Button>
          <IconButton onClick={() => loadFiles()} disabled={loading}>
            {loading && !openingPath ? <CircularProgress size={22} /> : <Refresh />}
          </IconButton>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {selectionMode && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleSelectAll}
                  disabled={items.length === 0 || bulkDeleting}
                />
              }
              label="Select all"
            />
            <Typography variant="body2" color="textSecondary">
              {selectedPaths.size} selected
            </Typography>
            <Box flexGrow={1} />
            <Button
              variant="contained"
              color="error"
              startIcon={bulkDeleting ? <CircularProgress color="inherit" size={18} /> : <DeleteSweep />}
              onClick={handleBulkDelete}
              disabled={selectedPaths.size === 0 || bulkDeleting}
            >
              Delete selected ({selectedPaths.size})
            </Button>
          </Stack>
        </Paper>
      )}

      {storage && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle1">Disk Usage</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatBytes(storage.free)} free of {formatBytes(storage.total)}
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={storage.percent_used} sx={{ height: 10, borderRadius: 1 }} />
                <Stack direction="row" gap={1} mt={1} flexWrap="wrap">
                  <Chip label={`Used: ${formatBytes(storage.used)}`} size="small" />
                  <Chip label={`Free: ${formatBytes(storage.free)}`} size="small" color="success" />
                  <Chip label={`${storage.percent_used}% used`} size="small" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1">Managed Root</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                  {storage.root}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }}>
          <TextField
            label="Search current folder"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1 }} /> }}
            sx={{ flexGrow: 1 }}
          />
          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} label="Sort By" onChange={(event) => setSortBy(event.target.value as SortBy)}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="size">Size</MenuItem>
              <MenuItem value="modified">Modified</MenuItem>
              <MenuItem value="type">Type</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Order</InputLabel>
            <Select value={sortOrder} label="Order" onChange={(event) => setSortOrder(event.target.value as SortOrder)}>
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={showHidden}
                onChange={(event) => setShowHidden(event.target.checked)}
              />
            }
            label="Show hidden"
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_event, value) => {
              if (value) setViewMode(value);
            }}
            size="small"
          >
            <ToggleButton value="list" aria-label="List view">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="Grid view">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" gap={1} mt={2} alignItems="center" flexWrap="wrap">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path || 'root'}>
              <Button size="small" onClick={() => loadFiles(crumb.path, crumb.path || '__root__')} disabled={Boolean(openingPath)}>
                {openingPath === (crumb.path || '__root__') ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                {crumb.label}
              </Button>
              {index < breadcrumbs.length - 1 && <Typography color="textSecondary">/</Typography>}
            </React.Fragment>
          ))}
        </Stack>
      </Paper>

      {viewMode === 'grid' ? (
        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : listing?.items.length ? (
            <Grid container spacing={2}>
              {listing.items.map((item) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={item.path}>
                  <Card
                    variant="outlined"
                    onClick={selectionMode ? () => toggleSelected(item.path) : undefined}
                    onDoubleClick={selectionMode ? undefined : () => openFolder(item)}
                    sx={{
                      height: '100%',
                      position: 'relative',
                      cursor: selectionMode || item.type === 'directory' ? 'pointer' : 'default',
                      overflow: 'hidden',
                      opacity: deletingPath === item.path ? 0.6 : 1,
                      outline: (theme) =>
                        selectionMode && selectedPaths.has(item.path)
                          ? `2px solid ${theme.palette.primary.main}`
                          : 'none',
                    }}
                  >
                    {selectionMode && (
                      <Checkbox
                        checked={selectedPaths.has(item.path)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleSelected(item.path)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          zIndex: 1,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          p: 0.25,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        height: 140,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {item.type === 'directory' ? (
                        openingPath === item.path ? (
                          <CircularProgress size={36} />
                        ) : (
                          <Folder color="primary" sx={{ fontSize: 64 }} />
                        )
                      ) : (
                        <ImageThumbnail item={item} />
                      )}
                    </Box>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography title={item.name} noWrap fontWeight={600} variant="body2">
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" noWrap>
                        {item.type === 'directory'
                          ? 'Folder'
                          : `${item.extension || 'file'} - ${item.size_known === false ? '-' : formatBytes(item.size)}`}
                      </Typography>
                      <Stack direction="row" justifyContent="flex-end" mt={1}>
                        {item.type === 'file' && (
                          <>
                            <IconButton
                              onClick={() => handlePreview(item)}
                              size="small"
                              disabled={Boolean(previewingPath || downloadingPath === item.path || deletingPath === item.path)}
                            >
                              {previewingPath === item.path ? <CircularProgress size={18} /> : <Visibility fontSize="small" />}
                            </IconButton>
                            <IconButton
                              onClick={() => handleDownload(item)}
                              size="small"
                              disabled={Boolean(downloadingPath || previewingPath === item.path || deletingPath === item.path)}
                            >
                              {downloadingPath === item.path ? <CircularProgress size={18} /> : <Download fontSize="small" />}
                            </IconButton>
                          </>
                        )}
                        <IconButton
                          onClick={() => handleDelete(item)}
                          size="small"
                          color="error"
                          disabled={Boolean(deletingPath || openingPath === item.path || previewingPath === item.path || downloadingPath === item.path)}
                        >
                          {deletingPath === item.path ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="textSecondary" textAlign="center" py={4}>
              No files or folders found
            </Typography>
          )}
        </Paper>
      ) : (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {selectionMode && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={toggleSelectAll}
                    disabled={items.length === 0 || bulkDeleting}
                  />
                </TableCell>
              )}
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Size</TableCell>
              <TableCell>Modified</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listing && listing.parent_path !== null && (
              <TableRow hover onClick={() => loadFiles(listing.parent_path || '', listing.parent_path || '__root__')} sx={{ cursor: 'pointer' }}>
                <TableCell colSpan={selectionMode ? 6 : 5}>
                  <Stack direction="row" gap={1} alignItems="center">
                    {openingPath === (listing.parent_path || '__root__') ? <CircularProgress size={20} /> : <Folder color="primary" />}
                    <Typography>..</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {loading ? (
              <TableRow>
                <TableCell colSpan={selectionMode ? 6 : 5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : listing?.items.length ? (
              listing.items.map((item) => (
                <TableRow
                  key={item.path}
                  hover
                  selected={selectionMode && selectedPaths.has(item.path)}
                  onClick={selectionMode ? () => toggleSelected(item.path) : undefined}
                  onDoubleClick={selectionMode ? undefined : () => openFolder(item)}
                  sx={{
                    cursor: selectionMode || item.type === 'directory' ? 'pointer' : 'default',
                    opacity: deletingPath === item.path ? 0.6 : 1,
                  }}
                >
                  {selectionMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPaths.has(item.path)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleSelected(item.path)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack direction="row" gap={1} alignItems="center">
                      {openingPath === item.path ? (
                        <CircularProgress size={20} />
                      ) : item.type === 'directory' ? (
                        <Folder color="primary" />
                      ) : (
                        <InsertDriveFile />
                      )}
                      <Typography fontWeight={item.type === 'directory' ? 600 : 400}>{item.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    {item.size_known === false ? '-' : formatBytes(item.size)}
                  </TableCell>
                  <TableCell>{new Date(item.modified_at).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {item.type === 'file' && (
                      <>
                        <IconButton
                          onClick={() => handlePreview(item)}
                          size="small"
                          disabled={Boolean(previewingPath || downloadingPath === item.path || deletingPath === item.path)}
                        >
                          {previewingPath === item.path ? <CircularProgress size={20} /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          onClick={() => handleDownload(item)}
                          size="small"
                          disabled={Boolean(downloadingPath || previewingPath === item.path || deletingPath === item.path)}
                        >
                          {downloadingPath === item.path ? <CircularProgress size={20} /> : <Download />}
                        </IconButton>
                      </>
                    )}
                    <IconButton
                      onClick={() => handleDelete(item)}
                      size="small"
                      color="error"
                      disabled={Boolean(deletingPath || openingPath === item.path || previewingPath === item.path || downloadingPath === item.path)}
                    >
                      {deletingPath === item.path ? <CircularProgress size={20} /> : <Delete />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={selectionMode ? 6 : 5} align="center">
                  <Typography color="textSecondary" py={4}>
                    No files or folders found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            value={newFolderName}
            onChange={(event) => setNewFolderName(event.target.value)}
            fullWidth
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleCreateFolder();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained" disabled={!newFolderName.trim() || creatingFolder}>
            {creatingFolder ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(preview)} onClose={closePreview} fullWidth maxWidth="lg">
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h6">{preview?.item.name}</Typography>
              {preview && (
                <Typography variant="body2" color="textSecondary">
                  {preview.contentType} - {formatBytes(preview.item.size)}
                </Typography>
              )}
            </Box>
            {preview && (
              <Button
                startIcon={downloadingPath === preview.item.path ? <CircularProgress color="inherit" size={18} /> : <Download />}
                onClick={() => handleDownload(preview.item)}
                disabled={downloadingPath === preview.item.path}
              >
                Download
              </Button>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 240 }}>
          {renderPreview()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FoldersPage;
